import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WorkspaceUsersService, CreateWorkspaceUserDTO } from "@/features/Workspaces/services/workspaceUsersService";

/**
 * Hook para obter os usuários completos associados a um produto
 * @param workspaceId ID do produto
 */
export const useWorkspaceUsers = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ['products-users', workspaceId],
    queryFn: () => workspaceId ? WorkspaceUsersService.getWorkspaceUsers(workspaceId) : [],
    enabled: !!workspaceId,
  });
};

/**
 * Hook para obter apenas os IDs dos usuários associados a um produto
 * @param workspaceId ID do produto
 */
export const useWorkspaceUserIds = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ['workspace-user-ids', workspaceId],
    queryFn: () => workspaceId ? WorkspaceUsersService.getWorkspaceUserIds(workspaceId) : [],
    enabled: !!workspaceId,
    staleTime: 0, // Sempre considerar os dados obsoletos
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

/**
 * Hook para atribuir um único usuário a um produto
 */
export const useAssignWorkspaceUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      workspaceId, 
      data 
    }: { 
      workspaceId: string; 
      data: CreateWorkspaceUserDTO;
    }) => WorkspaceUsersService.assignUser(workspaceId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-users', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', variables.workspaceId] });
      // Lista da aba de usuários do workspace (fonte v2 = membership direta) — sem
      // isto a tabela não reflete o membro recém-adicionado/removido até refetch manual.
      queryClient.invalidateQueries({ queryKey: ['workspace-members', variables.workspaceId] });
    }
  });
};

/**
 * Hook para atribuir múltiplos usuários a um produto de uma só vez
 */
export const useAssignWorkspaceUsers = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      workspaceId,
      userIds,
    }: {
      workspaceId: string;
      userIds: string[];
    }) => WorkspaceUsersService.assignUsers(workspaceId, { user_ids: userIds }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-users', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', variables.workspaceId] });
      // Lista da aba de usuários do workspace (fonte v2 = membership direta) — sem
      // isto a tabela não reflete o membro recém-adicionado/removido até refetch manual.
      queryClient.invalidateQueries({ queryKey: ['workspace-members', variables.workspaceId] });
    }
  });
};

/**
 * Hook para remover a atribuição de um usuário específico de um produto
 */
export const useUnassignWorkspaceUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      workspaceId, 
      userId 
    }: { 
      workspaceId: string; 
      userId: string;
    }) => WorkspaceUsersService.unassignUser(workspaceId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-users', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', variables.workspaceId] });
      // Lista da aba de usuários do workspace (fonte v2 = membership direta) — sem
      // isto a tabela não reflete o membro recém-adicionado/removido até refetch manual.
      queryClient.invalidateQueries({ queryKey: ['workspace-members', variables.workspaceId] });
    }
  });
};

/**
 * Hook para remover todas as atribuições de usuários de um produto
 */
export const useUnassignWorkspaceUsers = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (workspaceId: string) => 
      WorkspaceUsersService.unassignAllUsers(workspaceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-users', variables] });
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', variables] });
      queryClient.invalidateQueries({ queryKey: ['workspace-members', variables] });
    }
  });
};

/**
 * Hook para atualizar de forma atômica todos os usuários de um produto
 */
export const useUpdateWorkspaceUsers = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      workspaceId,
      userIds,
    }: {
      workspaceId: string;
      userIds: string[];
    }) =>
      WorkspaceUsersService.updateProductUsers(workspaceId, {
        user_ids: userIds,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-users', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', variables.workspaceId] });
      // Lista da aba de usuários do workspace (fonte v2 = membership direta) — sem
      // isto a tabela não reflete o membro recém-adicionado/removido até refetch manual.
      queryClient.invalidateQueries({ queryKey: ['workspace-members', variables.workspaceId] });
    },
  });
};
